import { defineStore } from 'pinia'
import { ref, computed, nextTick, watch } from 'vue'
import { useStorage } from "@vueuse/core"
import { useCustomerStore } from "@/stores/CustomersStore"
import { useModalStore } from "@/stores/ModalStore"
import { useEventStore } from "@/stores/EventsStore"
import { useCalendarStore } from '@/views/apps/calendar/useCalendarStore'
import { generateUniqueId } from "@/utils/strings"
import { useI18n } from "vue-i18n"
import { extractDate } from "@/utils/datetime"
import { useStatisticsSettingsStore } from "@/stores/statistics/StatisticsSettings"
import { abortRequestsGetThatStartsWith, isBackendRequestPendingThatStartWith } from "@/utils/backend"
import { saveBlob } from '@/utils/saveBlob'
import { parseFilenameFromContentDisposition } from "@/utils/contentDisposition"

export const useSalesStore = defineStore('salesStore', () => {
  const settingsStore = useStatisticsSettingsStore()
  const calendarStore = useCalendarStore()
  const modalStore = useModalStore()
  const eventStore = useEventStore()
  const customerStore = useCustomerStore()
  const { t } = useI18n()


  /**
   * Handles the sales click action.
   *
   * @async
   * @param {Number} saleId
   * @param {Boolean} closeOtherModals
   * @param {Boolean} clearSelectedCustomer
   */

  const handleSaleClick = async (saleId, closeOtherModals = false, clearSelectedCustomer = true) => {

    if (!saleId) {
      showErrorToast(t('sales.sale_cannot_be_found') + ' #2')

      return
    }
    // if (modalStore.modals.calendarSales || modalStore.modals.calendarEvent) { // There is already opened sales dialog
    //   // it can happen when 1. you open Sale dialog, 2. go to customer history, 3. click on other sale
    //   showWarningToast(t('sales.there_can_be_only_one_sale_dialog'))
    //
    //   return
    // }
    //
    // if (modalStore.modals.calendarSales) { // There is already opened event dialog
    //   showWarningToast(t('sales.there_can_be_only_one_sale_dialog'))
    //
    //   return
    // }


    try {
      if(closeOtherModals) {
        modalStore.resetModals()
      } else {
        modalStore.closeModal("calendarEvent")
        modalStore.closeModal("calendarSales")
      }
      await eventStore.resetSelectedEvent(clearSelectedCustomer) // Reset selected event in store
      calendarStore.eventIdToBeOpened = null // Clear any pending event to be opened on calendar
      modalStore.openModal("calendarSales")
      eventStore.isLoading = true
      eventStore.allowSave = false


      const currentSale = await fetchSale(saleId)


      // Handle nonexistant event
      if (!currentSale) {
        modalStore.closeModal("calendarSales")
        eventStore.isLoading = false

        return
      }

      eventStore.selectedEvent = currentSale.data
      customerStore.isCustomerBlacklisted = currentSale.data.client.blacklisted || false

      //console.log('Selected event:', currentSale.data)

      customerStore.customerLoyaltyPoints = currentSale.data.client?.loyaltyProgram?.loyaltyPoints || 0

      customerStore.loyaltyPointsEarned = currentSale.data.loyaltyProgramPoints?.gained || 0

      customerStore.customerVouchersAndTickets = currentSale.data.client?.vouchers // combined vouchers and tickets

      eventStore.selectedLoyaltyCoupon = currentSale.data.loyaltyProgramCoupon || null

      eventStore.visitConfirmed = currentSale.data.clientConfirmed
      eventStore.customerInSalon = currentSale.data.clientInSalon
      eventStore.selectedEvent.wantedNotif = currentSale.data.wantedNotif
      eventStore.isCyclical = !!eventStore.selectedEvent.cyclicalForFuture?.length


      eventStore.blockadeOrNoteView = false
      eventStore.visitView = true


      // // Prevent visit edit when setting "Zezwalaj na edytowanie terminarza z przeszłości" is disabled
      // if(!calendarStore.viewSettings.editPastEvents && currentSale.data.dateStart+" 23:59:59" < extractDate(new Date(), '-')+" 00:00:00") {
      //   eventStore.allowSave = false
      // }

      //B: tymczasowy workaround poki Lukasz nie wprowadzi jakis anonimowych kleintow
      const isAnonymous = (isEmpty(eventStore.selectedEvent.client.email) &&
        isEmpty(eventStore.selectedEvent.client.name) &&
        isEmpty(eventStore.selectedEvent.client.surname) &&
        isEmpty(eventStore.selectedEvent.client.phone))

      if (!isAnonymous && eventStore.selectedEvent.client.salonerClientId) {
        eventStore.selectedCustomer = {
          salonerClientId: eventStore.selectedEvent.client.salonerClientId,
          userEkey: eventStore.selectedEvent.client.userEkey,
          name: eventStore.selectedEvent.client.name,
          surname: eventStore.selectedEvent.client.surname,
          email: eventStore.selectedEvent.client.email,
          phone: eventStore.selectedEvent.client.phone,
          description: eventStore.selectedEvent.client.description,
        }
        // Customer events stat
        customerStore.customerStatistics = eventStore.selectedEvent.client.stats
      }

      // Products
      eventStore.selectedEvent.products?.forEach((product) => {
        addProductToSale(product)
      })

      // Prepayments
      if (currentSale.data.prepayments?.length > 0) {
        eventStore.selectedPrepaymentMethod = { ...currentSale.data.prepayments[0] }
        // Disable prepayment section if tpay was selected
        if (eventStore.selectedPrepaymentMethod.id === 8) eventStore.isPrepaymentSectionDisabled = true
        eventStore.selectedPrepaymentMethod.changedByHuman = true
      }
      // Payments
      if (currentSale.data.payments?.length > 0) {
        currentSale.data.payments.forEach((payment) => {
          //TODO B: wyjeb to
          if (payment.id === 7 || payment.id === 9) {
            payment.status = payment.checkStatus //B: Łukasz wprowadził kolumne status ktora mowi o tym czy bon jest aktwyny (1) czy nie (0) i to koliduje z tym co bylo juz na FE
          }
          const tmpPM = JSON.parse(JSON.stringify(calendarStore.paymentMethods.find(pm => pm.id === payment.id)))
          eventStore.selectedPaymentMethods.push({
            ...tmpPM,
            ...payment,
          })
        })
      }
      // Tip
      if (currentSale.data?.tip >= 0) {
        eventStore.tipAmount = currentSale.data.tip
      } else {
        eventStore.tipAmount = 0
      }

      eventStore.isLoading = false

      // Remove events from FC
      //eventStore.hideOriginalEventsOnFullcalendar()
      // Create new temporary events in place of original events
      //eventStore.updateSelectedEventOnFullcalendar()

      // ACTIONS
      // eventStore.actions.showSave = !eventStore.blockadeOrNoteView && !eventStore.wasFinalisedAlready && !eventStore.showFinalization &&
      // ((eventStore.selectedEvent.id && eventStore.allowSave) || !eventStore.isCyclical)
      //console.warn('actions', currentSale.data.actions)

      eventStore.allowSave = false //currentSale.data.actions?.save
      eventStore.showFinalization = true

      eventStore.actions.showSave = false //eventStore.allowSave
      eventStore.actions.showEdit = currentSale.data.actions?.edit
      eventStore.actions.showDelete = currentSale.data.actions?.delete
      eventStore.actions.showCancel = currentSale.data.actions?.cancel
      eventStore.actions.showRestore = false //currentSale.data.actions?.restore


    } catch (error) {
      modalStore.closeModal("calendarSales")
      eventStore.isLoading = false

      console.error("Error occurred while fetching event", error)
    }


  }

  //  👉 Add product to sale
  const addProductToSale = (product) => {
    let employeeThatSold = calendarStore.employeesCalendars.find(cal => cal.id === product.employeeEkey)
    if (!employeeThatSold && calendarStore.employeesCalendars.length > 0) {
      employeeThatSold = calendarStore.employeesCalendars[0]
    }
    const totalValue = Number(product.price) * Number(product.quantity)
    const finalPrice = totalValue - Number(product.discount)
    let newProduct = {
      id: product.id,
      title: product.title,
      unit: product.unit,
      price: product.price,
      quantity: product.quantity,
      capacity: product.capacity,
      discountType: !product.discountType ? "solid" : product.discountType,
      discountValue: product.discountValue ?? 0,
      discount: product.discount,
      type: 1, // always 1 = sales (not usage)
      finalPrice: finalPrice,
      seller: employeeThatSold,
      voucherDetails: product.voucherDetails || null,
      isVoucher: product.isVoucher || null,
      dynamicId: generateUniqueId(),
    }
    if (product.voucherEntityId && product.voucherDetails) {
      newProduct = {
        ...newProduct,
        voucher: 1,
        identifier: product.voucherDetails.identifier,
        voucherType: Number(product.voucherDetails.type),
        voucherValue: Number(product.voucherDetails.value),
        isValueEditable: false,
      }
      if (Number(product.voucherDetails.type) === 2) { // KARNET
        newProduct = {
          ...newProduct,
          services: product.voucherDetails.services,
        }
      }
    }
    if (product.isVoucher) { // KARNET lub BON
      newProduct = {
        ...newProduct,
        voucherExpirationDate: new Date(product.voucherDetails.timeout),
      }
      // B: there is no need to update .voucherDetails.timeout here cause its in "2025-01-29" format already
    }
    eventStore.selectedProducts.push(newProduct)
  }


  const fetchSale = async (saleId) => {
    return await $backend(`/productSales/show/${saleId}`, {
      method: 'GET',
    })
  }


  /**
   * Start date for statistics date range filter
   * Derived from centralized StatisticsSettingsStore
   */
  const rangeStart = computed(() => settingsStore.rangeStart)
  /**
   * End date for statistics date range filter
   * Derived from centralized StatisticsSettingsStore
   */
  const rangeEnd = computed(() => settingsStore.rangeEnd)

  const isLoading = ref(false)
  const salesStatisticsData = ref(null)
  const salesStatisticsHasLoaded = ref(false)

  const isDownloading = ref(false)


  // Query params:
  // perPage - default 100
  // page - default 3
  // filters[employeeEkey] - example: pybvwmxpxmrp
  // filters[salonerClient] - example: ando
  // filters[product]
  // filters[dateStart] - 2025-11-03 - Format Y-m-d
  // filters[dateEnd] - 2025-11-03 - Format Y-m-d
  // orders[0][column] - default: createTime || Available options: "createTime", "salonerClient", "totalPrice"
  // orders[0][direction] - default: desc || Available options: "asc", "desc"

  // Use individual useStorage for persistence
  // const salesTablePage = useStorage('sale-statistics-page', 1, localStorage, {
  //   listenToStorageChanges: false,
  // })
  // const salesTablePerPage = useStorage('sale-statistics-perPage', 10, localStorage, {
  //   listenToStorageChanges: false,
  // })

  const DEFAULT_SORT = {
    key: 'createTime',
    order: 'desc',
  }

  const tableOptions = ref({
    page: useStorage('sale-statistics-page', 1, localStorage, {
      listenToStorageChanges: false,
    }),
    perPage: useStorage('sale-statistics-perPage', 10, localStorage, {
      listenToStorageChanges: false,
    }),
    total: 0,
    totalAbsolute: 0,
    filters: {
      employeeEkey: useStorage('sale-statistics-employeeEkey', null, localStorage, {
        listenToStorageChanges: false,
      }),
      salonerClient: useStorage('sale-statistics-salonerClient', '', localStorage, {
        listenToStorageChanges: false,
      }),
      product: useStorage('sale-statistics-product', '', localStorage, {
        listenToStorageChanges: false,
      }),
    },
    //Backend sorts by createTime - desc by default.
    //Sales Table: Currently uses createTime as the default key. Since createTime is a visible column in the headers,
    //Vuetify's VDataTableServer automatically displays an active sort icon in that column header on page load.
    sortBy: (() => {
      const stored = localStorage.getItem('salestable-selected-sorting-option')
      if (stored) {
        const [key, order] = stored.split('-')

        return [{ key, order }]
      }

      return [{ ...DEFAULT_SORT }]// default
    })(),
  })

  const resetSorting = () => {
    tableOptions.value.sortBy = [{ ...DEFAULT_SORT }]

    return tableOptions.value.sortBy
  }

  /**
   * Helper to build URL parameters for sales statistics requests
   * @param {Object} options - Optional overrides for pagination and sorting
   * @returns {URLSearchParams}
   */
  const buildSalesParams = (options = {}) => {
    const params = new URLSearchParams()
    const opt = tableOptions.value

    // 1. Pagination (Only if applicable, usually for list view)
    if (options.page || opt.page) params.append('page', options.page || opt.page)
    if (options.perPage || opt.perPage) params.append('perPage', options.perPage || opt.perPage)

    // 2. Date Filters
    if (rangeStart.value) params.append('filters[dateStart]', extractDate(new Date(rangeStart.value), '-'))
    if (rangeEnd.value) params.append('filters[dateEnd]', extractDate(new Date(rangeEnd.value), '-'))

    // 3. Other Filters
    if (opt.filters.employeeEkey) params.append('filters[employeeEkey]', opt.filters.employeeEkey)
    if (opt.filters.salonerClient) params.append('filters[salonerClient]', opt.filters.salonerClient)
    if (opt.filters.product) params.append('filters[product]', opt.filters.product)

    // 4. Sorting logic
    const sortBy = options.sortBy || opt.sortBy
    if (sortBy && sortBy.length) {
      sortBy.forEach((sort, index) => {
        params.append(`orders[${index}][column]`, sort.key)
        params.append(`orders[${index}][direction]`, sort.order)
      })
    }

    return params
  }

  // Get sales statistics page data
  const getSalesStatisticsPageData = async (options) => {
    try {
      abortRequestsGetThatStartsWith('/productSales/list')
    } catch (error) {
    }

    isLoading.value = true

    try {
      const params = buildSalesParams(options) // Uses current tableOptions state
      const queryString = params.toString() ? `?${params.toString()}` : ''

      const response = await $backend(`/productSales/list${queryString}`, {
        method: 'GET',
      })

      if (response.success) {

        if (response.data.sales.length === 0 && tableOptions.value.page !== 1 && response.meta?.total > 0) {
          const lastPage = Math.max(1, Math.ceil(response.meta.total / tableOptions.value.perPage))
          // CRITICAL: sync items-length BEFORE changing the page. VDataTableServer derives
          // pageCount from :items-length, and an internal watcher clamps page back down when
          // page > pageCount. While totalCustomers is still 0/stale, pageCount is 1, so setting
          // the page to e.g. 2 would be immediately clamped to 1 (see vuetify paginate.js).
          tableOptions.value.total = response.meta.total
          tableOptions.value.totalAbsolute = response.meta.totalAll
          tableOptions.value.page = lastPage

          return true
        }


        salesStatisticsData.value = response.data || null
        salesStatisticsHasLoaded.value = true
        tableOptions.value.total = response.meta.total
        tableOptions.value.totalAbsolute = response.meta.totalAll

        isLoading.value = false

        return true
      }
    } catch (error) {
      if (!isBackendRequestPendingThatStartWith('/productSales/list')) isLoading.value = false


      return false
    }

    isLoading.value = false
  }

  /**
   * Drops the loaded list, so re-entering the page doesn't flash stale sales.
   * No abort here - SoldProductsPreviewStore shares the /productSales/list prefix.
   */
  const clearSalesStatistics = () => {
    salesStatisticsData.value = null
    salesStatisticsHasLoaded.value = false
    tableOptions.value.total = 0
    tableOptions.value.totalAbsolute = 0
  }

  const deleteProductSale = async (id) => {
    if (!id) return
    isLoading.value = true
    try {
      const response = await $backend(`/productSales/delete`, {
        method: 'POST',
        body: { id },
      })

      if (response.success) {
        await getSalesStatisticsPageData()

        return response
      }
    } catch (e) {
    }
    isLoading.value = false

    return { success: false }
  }

  const exportData = async (options) => {
    if (isDownloading.value)
      return false

    isDownloading.value = true
    try {
      // Pass options to override default sorting if necessary
      const params = buildSalesParams(options)

      // Export usually doesn't need pagination, so we can remove those keys if the API requires it
      params.delete('page')
      params.delete('perPage')

      const queryString = params.toString() ? `?${params.toString()}` : ''

      const response = await $backend.raw(
        `/productSales/export${queryString}`,
        {
          method: 'GET',
          responseType: 'blob',
          disableAbortController: true,
        },
      )

      const blob = response._data
      const fallbackFilename = `statystyki_sprzedaz_produktow_${extractDate(new Date(), '-')}.xlsx`
      const filename = parseFilenameFromContentDisposition(response.headers.get('content-disposition')) || fallbackFilename

      return await saveBlob(blob, filename)
    } catch (e) {
      console.error(
        'Nie udało się wyeksportować statystyk sprzedanych produktów:',
        error,
      )

      return false
    } finally {
      isDownloading.value = false
    }
  }

  return {
    rangeStart,
    rangeEnd,
    isLoading,
    salesStatisticsData,
    salesStatisticsHasLoaded,
    isDownloading,
    getSalesStatisticsPageData,
    clearSalesStatistics,
    tableOptions,
    resetSorting,
    handleSaleClick,
    fetchSale,
    exportData,
    deleteProductSale,
  }
})
